import { Search as SearchIcon } from '@mui/icons-material';
import { Box, SxProps, Theme } from '@mui/material';
import React, { ComponentProps, ReactNode, Ref } from 'react';
import { TextInput } from 'src/UIKit/components/TextInput';

export const TABLE_HEADER_CONTROL_HEIGHT = 40;

/** Shared style for a TableHeader action button (e.g. "Create X"). */
export const TABLE_HEADER_ACTION_SX: SxProps<Theme> = {
  height: TABLE_HEADER_CONTROL_HEIGHT,
  whiteSpace: 'nowrap',
  width: { xs: '100%', sm: 'auto' },
};

/** Wrapper for multiple TableHeader action buttons. */
export const TABLE_HEADER_ACTIONS_WRAP_SX: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
  flexWrap: 'wrap',
};

interface TableHeaderProps {
  searchValue?: string | null;
  onSearchChange?: ComponentProps<typeof TextInput>['onChange'];
  searchPlaceholder?: string;
  searchInputRef?: Ref<HTMLInputElement>;
  searchDisabled?: boolean;
  filters?: ReactNode;
  actions?: ReactNode;
}

/**
 * Standard header for GridData tables: search input, optional filters and
 * right-aligned actions laid out on a single wrapping row with uniform
 * spacing. Wrap each filter in a TableHeaderItem to get the standard sizing.
 */
export const TableHeader = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
  searchInputRef,
  searchDisabled,
  filters,
  actions,
}: TableHeaderProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {!!onSearchChange && (
        <Box sx={{ flex: '1 1 150px', minWidth: 150, maxWidth: 300 }}>
          <TextInput
            ref={searchInputRef}
            placeholder={searchPlaceholder}
            endIcon={<SearchIcon />}
            value={searchValue}
            onChange={onSearchChange}
            disabled={searchDisabled}
            margin="none"
            sx={{ height: TABLE_HEADER_CONTROL_HEIGHT, pr: 1.5 }}
          />
        </Box>
      )}

      {filters}

      {!!actions && (
        <Box sx={{ width: { xs: '100%', sm: 'auto' }, ml: { sm: 'auto' } }}>{actions}</Box>
      )}
    </Box>
  );
};

export const TableHeaderItem = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      flex: '1 1 150px',
      minWidth: 100,
      maxWidth: 300,
      '& .MuiInputBase-root': { height: TABLE_HEADER_CONTROL_HEIGHT },
    }}
  >
    {children}
  </Box>
);

export default TableHeader;
